import Cookies from 'js-cookie';
import React from 'react';

import { AuthUser, getBaseUrl, useRefreshToken } from '@/api';
import { AuthLoaderStyled } from '@/auth/components';
import { shouldRefreshToken } from '@/auth/duck';

import { AuthContext } from '.';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = React.useState<string>('');
  const [tokenExpiresAt, setTokenExpiresAt] = React.useState<number | null>(null);

  const baseUrl = React.useMemo(() => getBaseUrl(), []);
  const refreshTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const { refreshToken, refreshedTokenData } = useRefreshToken();

  const AUTH_URL = `${baseUrl}/api/auth/login`;
  const REDIRECT_URL = `${origin}/welcome`;
  const CALLBACK_URL = `${origin}/auth/callback`;
  const LOGOUT_URL = `${baseUrl}/api/auth/logout`;

  const logout = React.useCallback((redirect: boolean = true) => {
    setIsLoggedIn(false);
    setUser(null);
    setAccessToken('');
    setTokenExpiresAt(null);

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    Cookies.remove('accessToken');
    Cookies.remove('tokenExpiresAt');
    localStorage.removeItem('user');
    localStorage.removeItem('plannedTokenRefreshAt');

    if (redirect) {
      window.location.href = `${LOGOUT_URL}?redirect_uri=${REDIRECT_URL}`;
    }
  }, [LOGOUT_URL, REDIRECT_URL]);

  const handleLoginSuccess = React.useCallback((
    token: string,
    userData: AuthUser,
    expiresIn: number,
  ) => {
    setAccessToken(token);
    setUser(userData);
    setIsLoggedIn(true);

    const expiresAt = Date.now() + expiresIn * 1000;
    setTokenExpiresAt(expiresAt);

    Cookies.set('accessToken', token, { secure: true, sameSite: 'strict' });
    Cookies.set('tokenExpiresAt', expiresAt.toString(), { secure: true, sameSite: 'strict' });
    localStorage.setItem('user', JSON.stringify(userData));
  }, []);

  React.useEffect(() => {
    const savedToken = Cookies.get('accessToken');
    const savedUser = localStorage.getItem('user');
    const savedExpiresAt = Cookies.get('tokenExpiresAt');

    if (savedToken && savedUser) {
      setAccessToken(savedToken);
      setUser(JSON.parse(savedUser));
      setIsLoggedIn(true);
      if (savedExpiresAt) {
        setTokenExpiresAt(parseInt(savedExpiresAt, 10));
      }
    }
    setIsInitialized(true);
  }, []);

  React.useEffect(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    if (isLoggedIn && tokenExpiresAt) {
      const timeRemaining = tokenExpiresAt - Date.now();
      const refreshBuffer = 1000 * 60 * 5;
      const delay = Math.max(timeRemaining - refreshBuffer, 0);

      refreshTimerRef.current = setTimeout(() => {
        refreshToken().catch(() => logout(true));
      }, delay);
    }

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [isLoggedIn, tokenExpiresAt, refreshToken, logout]);

  React.useEffect(() => {
    const syncAuth = (event: StorageEvent) => {
      if (event.key === 'user' && event.newValue === null) {
        logout(false);
        window.location.href = REDIRECT_URL;
        return;
      }

      if (event.key === 'user' && event.newValue !== null) {
        window.location.reload();
      }
    };

    window.addEventListener('storage', syncAuth);
    return () => window.removeEventListener('storage', syncAuth);
  }, [logout, REDIRECT_URL]);

  React.useEffect(() => {
    const handleFocus = () => {
      if (!isLoggedIn) {
        return;
      }
      const expiresAt = Cookies.get('tokenExpiresAt');
      if (expiresAt && shouldRefreshToken(parseInt(expiresAt, 10))) {
        refreshToken().catch(() => logout(true));
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isLoggedIn, refreshToken, logout]);

  React.useEffect(() => {
    if (refreshedTokenData && user) {
      handleLoginSuccess(refreshedTokenData.accessToken, user, refreshedTokenData.expiresIn);
    }
  }, [handleLoginSuccess, refreshedTokenData, user]);

  const login = React.useCallback(() => {
    document.location = `${AUTH_URL}?response_type=code&&redirect_uri=${CALLBACK_URL}`;
  }, [AUTH_URL, CALLBACK_URL]);

  if (!isInitialized) {
    return (
      <div
        style={{
          display: 'grid',
          width: '100vw',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AuthLoaderStyled />
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        user,
        accessToken,
        login,
        logout,
        handleLoginSuccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
